'use client';

import { type CSSProperties, type ReactNode } from 'react';
import { ArrowRight, Loader2, MessageSquareText } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Badge } from '@/components/ui/data-display/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/layout/popover';
import { cn } from '@/lib/utils';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

// ---------------------------------------------------------------------------
// Priority helpers
// ---------------------------------------------------------------------------

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

function highestPriority(
  suggestions: ResumeAnalysisSuggestion[]
): 'high' | 'medium' | 'low' {
  let best: 'high' | 'medium' | 'low' = 'low';
  for (const s of suggestions) {
    if (PRIORITY_ORDER[s.priority] < PRIORITY_ORDER[best]) best = s.priority;
  }
  return best;
}

const DOT_COLORS: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500',
};

const PRIORITY_BADGE_VARIANT: Record<string, 'destructive' | 'warning' | 'success'> = {
  high: 'destructive',
  medium: 'warning',
  low: 'success',
};

// ---------------------------------------------------------------------------
// AnalysisCommentIndicator
// ---------------------------------------------------------------------------

interface AnalysisCommentIndicatorProps {
  suggestions: ResumeAnalysisSuggestion[];
  onApply: (suggestion: ResumeAnalysisSuggestion) => void;
  onViewReport?: () => void;
  isApplying?: string | null;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function AnalysisCommentIndicator({
  suggestions,
  onApply,
  onViewReport,
  isApplying,
  disabled,
  className,
  style,
}: AnalysisCommentIndicatorProps) {
  if (suggestions.length === 0) return null;

  const priority = highestPriority(suggestions);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'relative flex h-5 w-5 items-center justify-center rounded-full transition-all duration-200',
            'hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
            DOT_COLORS[priority],
            'animate-in fade-in-0 zoom-in-75 duration-300',
            className
          )}
          style={style}
          title={`${suggestions.length} analysis suggestion${suggestions.length > 1 ? 's' : ''}`}
        >
          <MessageSquareText className="h-3 w-3 text-white" />
          {suggestions.length > 1 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-[9px] font-bold leading-none text-background">
              {suggestions.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="w-80 p-0"
      >
        <div className="max-h-72 overflow-y-auto divide-y divide-border">
          {suggestions.map((suggestion) => (
            <div key={suggestion.id} className="p-3 space-y-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-label text-xs leading-tight">
                    {suggestion.title}
                  </span>
                  <Badge
                    variant={PRIORITY_BADGE_VARIANT[suggestion.priority]}
                    className="text-[9px] px-1 py-0"
                  >
                    {suggestion.priority}
                  </Badge>
                  {suggestion.estimatedScoreGain > 0 && (
                    <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                      +{suggestion.estimatedScoreGain} pts
                    </span>
                  )}
                </div>
                <p className="text-caption text-muted-foreground mt-1 leading-snug">
                  {suggestion.reason}
                </p>
              </div>
              <div className="flex items-center justify-between">
                {onViewReport && (
                  <button
                    type="button"
                    onClick={onViewReport}
                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    View full report
                    <ArrowRight className="h-3 w-3" />
                  </button>
                )}
                <div className={cn(!onViewReport && 'ml-auto')}>
                  {suggestion.canAutoApply ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2.5 text-xs"
                      disabled={disabled || isApplying === suggestion.id}
                      onClick={() => onApply(suggestion)}
                    >
                      {isApplying === suggestion.id ? (
                        <>
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          Applying
                        </>
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  ) : (
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      Manual
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// WithCommentIndicator wrapper
// ---------------------------------------------------------------------------

interface WithCommentIndicatorProps {
  suggestions: ResumeAnalysisSuggestion[];
  onApply: (suggestion: ResumeAnalysisSuggestion) => void;
  onViewReport?: () => void;
  isApplying?: string | null;
  disabled?: boolean;
  children: ReactNode;
}

export function WithCommentIndicator({
  suggestions,
  onApply,
  onViewReport,
  isApplying,
  disabled,
  children,
}: WithCommentIndicatorProps) {
  if (suggestions.length === 0) return <>{children}</>;

  return (
    <div className="relative flex items-center">
      <AnalysisCommentIndicator
        suggestions={suggestions}
        onApply={onApply}
        onViewReport={onViewReport}
        isApplying={isApplying}
        disabled={disabled}
        className="-ml-1 mr-1 shrink-0 z-10"
      />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
